import React, { createContext, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import Page from "../../components/page";
import SignUpForm from "./components/signUpForm";
import { useRouteSlug } from "../../hooks";
import { withPageInitialLoad } from "../../libs/pageDataBridge";

import useIsSmallScreen from "@/hooks/useIsSmallScreen";
import AuthMobileLayout from "@/components/auth/authMobileLayout";
import AuthWebLayout from "@/components/auth/authWebLayout";
import TermsAndPrivacy from "@/components/auth/termsAndPrivacy";
import { getAuthEmail } from "@/util/auth";
import OtpFlow from "./components/otpFlow";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  selectGetResponseUpdateDetails,
  selectIsConflictMobileFlow
} from "@/util/selectors";
import {
  authenticateUserByEmail,
  initiateMobileVerification,
  setConflictMobileFlow,
  startRegistration
} from "@/redux/actions/homepage.action";
import UpdateDetails from "@/components/auth/updateDetailsSocial";
import { DRAFT_STORAGE_KEY, SIGNUP_API_CODE_NAMES } from "@/constants";
import DialogModal from "@/components/dialogModal";
import { clearFromSessionStorage } from "@/util/storage";

import "./style.scss";

export enum SIGN_UP_FLOW_STEP {
  CREATE = "create",
  OTP = "otp",
  UPDATE = "update"
}

const SignUpPage = () => {
  const [activeFlowStep, setActiveFlowStep] = useState<SIGN_UP_FLOW_STEP>(
    SIGN_UP_FLOW_STEP.CREATE
  );
  const [errorMessage, setErrorMessage] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [showMobileConflictModal, setShowMobileConflictModal] = useState(false);
  const [conflictMobileResponse, setConflictMobileResponse] = useState(null);
  const [prefill, setPrefill] = useState({
    name: "",
    phone: "",
    areaCode: "",
    password: ""
  });

  const { t } = useTranslation(["signUp", "seo"]);
  const routeSlug = useRouteSlug();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isSmallScreen } = useIsSmallScreen();
  const validatedUserEmail = getAuthEmail();
  const socialSignUpResponse = useAppSelector(selectGetResponseUpdateDetails);
  const [conflictModalOtpOpened, setConflictModalOtpOpened] = useState(false);
  const isConflictMobileFlow = useAppSelector(selectIsConflictMobileFlow);

  const checkEmail = async () =>
    await dispatch(
      authenticateUserByEmail({
        email: validatedUserEmail
      })
    );

  useEffect(() => {
    if (validatedUserEmail && !socialSignUpResponse) {
      checkEmail();
    } else if (socialSignUpResponse) {
      setActiveFlowStep(SIGN_UP_FLOW_STEP.UPDATE);
    } else {
      history.push(`/${routeSlug}/authorization/`);
    }
  }, [validatedUserEmail, socialSignUpResponse]);

  const backClick = () => {
    if (activeFlowStep === SIGN_UP_FLOW_STEP.OTP) {
      setActiveFlowStep(
        socialSignUpResponse
          ? SIGN_UP_FLOW_STEP.UPDATE
          : SIGN_UP_FLOW_STEP.CREATE
      );
    }
    if (activeFlowStep === SIGN_UP_FLOW_STEP.CREATE) {
      history.push(`/${routeSlug}/authorization/`);
    }
    setConflictModalOtpOpened(false);
  };

  const handleSocialRegistration = async data => {
    try {
      const submitData = {
        token: data?.token,
        fullName: data?.bodyData?.fullname.trim(),
        phone: data?.bodyData?.phone,
        loyaltyConsent: true
      };

      await startRegistrationProcess(submitData);
    } catch (error) {
      console.error("Sign Up error", error);
    }
  };

  const startRegistrationProcess = useCallback(
    async (data: any) => {
      dispatch(setConflictMobileFlow(false));
      const response = await dispatch(startRegistration(data));

      if (SIGNUP_API_CODE_NAMES.OTP_REQUIRED === response.code) {
        setResponseData(response);
        setActiveFlowStep(SIGN_UP_FLOW_STEP.OTP);
      }

      if (
        SIGNUP_API_CODE_NAMES.MOBILE_ALREADY_USED_IN_ANOTHER_ACCOUNT ===
        response.data.code
      ) {
        dispatch(setConflictMobileFlow(true));
        setResponseData(response.data);
        setShowMobileConflictModal(true);
      }

      if (!response?.data?.success && response?.data?.message) {
        setErrorMessage(response?.data?.message);
      }

      if (Number(response.code) === 200 && response.customerId) {
        history.push(`/${routeSlug}/`);
      }
      return response;
    },
    [dispatch, history, routeSlug]
  );

  const handleResendOtp = async () => {
    const response = await dispatch(
      initiateMobileVerification(responseData.token)
    );

    if (SIGNUP_API_CODE_NAMES.OTP_REQUIRED === response.code) {
      setResponseData(response);
      setActiveFlowStep(SIGN_UP_FLOW_STEP.OTP);
    }
    setConflictModalOtpOpened(true);
    setShowMobileConflictModal(false);
  };

  useEffect(() => {
    return () => {
      clearFromSessionStorage(DRAFT_STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    if (socialSignUpResponse) setConflictMobileResponse(socialSignUpResponse);
  }, [socialSignUpResponse]);

  const contentRender = () => {
    let content = null;

    switch (activeFlowStep) {
      case SIGN_UP_FLOW_STEP.CREATE:
        content = (
          <div className="sign_create">
            <h3 className="title">{t("signUp:createAccount")}</h3>
            {isSmallScreen && <span className="back-btn" onClick={backClick} />}
            <p className="signinUpWith">
              {t("singinUpWith")}{" "}
              <span className="validatedEmail">{validatedUserEmail}</span>{" "}
              <Link className="editLink" to={`/${routeSlug}/authorization/`}>
                {t("edit")}
              </Link>
            </p>
            <SignUpForm
              setConflictModalOtpOpened={setConflictModalOtpOpened}
              setOtpData={setResponseData}
              persistedValues={prefill}
              onPersist={partial =>
                setPrefill(prev => ({ ...prev, ...partial }))
              }
            />
            <TermsAndPrivacy />
          </div>
        );
        break;
      case SIGN_UP_FLOW_STEP.OTP:
        content = (
          <div className="signUp_OTP">
            {isSmallScreen && <span className="back-btn" onClick={backClick} />}
            <OtpFlow
              verificationPhone={responseData?.maskedPhoneNumber}
              otpResendTimeout={responseData?.retryAfterSeconds}
              otpToken={responseData?.token}
              backRedirection={backClick}
            />
            {!conflictModalOtpOpened && <TermsAndPrivacy />}
          </div>
        );
        break;
      default:
        content = (
          <div>
            <UpdateDetails
              onConfirm={handleSocialRegistration}
              onBack={() => history.push(`/${routeSlug}/authorization/`)}
            />
            <TermsAndPrivacy />
            <DialogModal
              classname="mobile_conflict_modal"
              bodyText={errorMessage}
              cancelButtonText={t("cancel")}
              confirmButtonText={t("update")}
              showModal={showMobileConflictModal}
              onCancel={() => {
                setShowMobileConflictModal(false);
                setConflictModalOtpOpened(false);
              }}
              onConfirm={handleResendOtp}
            />
          </div>
        );
    }

    return <div>{content}</div>;
  };
  return (
    <Page
      title={t("seo:joinTitle")}
      id="signup"
      className="register_user_page_wrapper"
    >
      <SignUpContext.Provider
        value={{
          setActiveFlowStep,
          setErrorMessage,
          errorMessage,
          responseData,
          setResponseData
        }}
      >
        {isSmallScreen ? (
          <AuthMobileLayout signUp className="signUpLayout">
            {contentRender()}
          </AuthMobileLayout>
        ) : (
          <AuthWebLayout signUp className="signUpLayout">
            {contentRender()}
          </AuthWebLayout>
        )}
      </SignUpContext.Provider>
    </Page>
  );
};

export const SignUpContext = createContext<{
  setActiveFlowStep: (step: SIGN_UP_FLOW_STEP) => void;
  setErrorMessage: (error: string) => void;
  responseData: any;
  setResponseData: (response: any) => void;
  errorMessage: string;
}>(null);

export default withPageInitialLoad({
  pageName: "Signup",
  page: SignUpPage,
  ssr: false,
  loader: false
});
